import type { ExerciseAttempt } from '@/entities/attempt'
import { createNourishmentEvent } from '@/entities/plant-lifecycle'
import type {
  ForestTreeOriginV2,
  PlantGardenV2Snapshot,
  PlantRewardResultV2,
  PlantV2MutationResult,
} from '@/entities/plant-garden-v2'
import type { PlantSpecies } from '@/types/plantTypes'
import { usePlantGardenV2Store } from '@/store/plantGardenV2Store'
import { createPlantGardenV2Repository } from './createPlantGardenV2Repository'
import type { PlantGardenV2Repository } from './PlantGardenV2Repository'

export class PlantGardenV2ConflictError extends Error {
  constructor() {
    super('森林刚刚在另一个页面发生变化，已经载入最新状态，请重试。')
    this.name = 'PlantGardenV2ConflictError'
  }
}

export class PlantGardenV2Runtime {
  private visibleStudentId: string | null = null
  private readonly snapshots = new Map<string, PlantGardenV2Snapshot>()
  private readonly reconciledAttemptIds = new Set<string>()
  private queue: Promise<void> = Promise.resolve()

  constructor(
    private readonly repository: PlantGardenV2Repository,
    private readonly publish: (studentId: string, snapshot: PlantGardenV2Snapshot) => void,
  ) {}

  loadGarden(studentId: string): Promise<PlantGardenV2Snapshot> {
    this.visibleStudentId = studentId
    return this.enqueue(() => this.loadNow(studentId))
  }

  applyRewardClaim(studentId: string, rewardClaimId: string): Promise<PlantRewardResultV2> {
    return this.enqueue(async () => {
      const result = await this.repository.applyRewardClaim(studentId, rewardClaimId)
      await this.loadNow(studentId)
      return result
    })
  }

  reconcileNourishmentAttempts(studentId: string, attempts: readonly ExerciseAttempt[]): Promise<void> {
    return this.enqueue(async () => {
      for (const attempt of attempts) {
        if (attempt.studentId !== studentId || this.reconciledAttemptIds.has(attempt.id)) continue
        if (!createNourishmentEvent(attempt)) continue
        const result = await this.repository.applyNourishmentAttempt(studentId, attempt.id)
        this.reconciledAttemptIds.add(attempt.id)
        if (result.status === 'applied') await this.loadNow(studentId)
      }
    })
  }

  plantSeed(
    studentId: string,
    species: PlantSpecies,
    parcelIndex: number,
    slotIndex: number,
    gridX?: number,
    gridY?: number,
  ) {
    return this.command(studentId, (snapshot) => this.repository.plantSeed(studentId, {
      expectedRevision: snapshot.revision, species, parcelIndex, slotIndex, gridX, gridY,
    }))
  }

  moveTree(
    studentId: string,
    treeOrigin: ForestTreeOriginV2,
    treeId: string,
    parcelIndex: number,
    slotIndex: number,
    gridX?: number,
    gridY?: number,
  ) {
    return this.command(studentId, (snapshot) => this.repository.moveTree(studentId, {
      expectedRevision: snapshot.revision, treeOrigin, treeId, parcelIndex, slotIndex, gridX, gridY,
    }))
  }

  uprootDeadTree(studentId: string, treeOrigin: ForestTreeOriginV2, treeId: string) {
    return this.command(studentId, (snapshot) => this.repository.uprootDeadTree(studentId, {
      expectedRevision: snapshot.revision, treeOrigin, treeId,
    }))
  }

  unlockNextParcel(studentId: string) {
    return this.command(studentId, (snapshot) => (
      this.repository.unlockNextParcel(studentId, snapshot.revision)
    ))
  }

  private command(
    studentId: string,
    execute: (snapshot: PlantGardenV2Snapshot) => Promise<PlantV2MutationResult>,
  ): Promise<PlantGardenV2Snapshot> {
    return this.enqueue(async () => {
      const current = this.snapshots.get(studentId) ?? await this.loadNow(studentId)
      try {
        const result = await execute(current)
        if (result.status === 'conflict') {
          await this.loadNow(studentId)
          throw new PlantGardenV2ConflictError()
        }
        return await this.loadNow(studentId)
      } catch (error) {
        if ((error as { status?: number }).status === 409) {
          await this.loadNow(studentId)
          throw new PlantGardenV2ConflictError()
        }
        throw error
      }
    })
  }

  private async loadNow(studentId: string) {
    const snapshot = await this.repository.loadGarden(studentId)
    const current = this.snapshots.get(studentId)
    if (!current || current.revision <= snapshot.revision) this.snapshots.set(studentId, snapshot)
    if (this.visibleStudentId === studentId) this.publish(studentId, snapshot)
    return snapshot
  }

  private enqueue<T>(operation: () => Promise<T>): Promise<T> {
    const result = this.queue.then(operation, operation)
    this.queue = result.then(() => undefined, () => undefined)
    return result
  }
}

const runtime = new PlantGardenV2Runtime(
  createPlantGardenV2Repository(),
  (studentId, snapshot) => usePlantGardenV2Store.setState({ studentId, snapshot }),
)

export function getPlantGardenV2Runtime() {
  return runtime
}
